import { useForm, FormProvider, Controller } from "react-hook-form";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import type { SourceFile } from "../sources-types";
import { FormLabel, FTPInput, NotesTextarea } from "../../../../../components-new";
import { formSubtitleClass } from "./addSourceStyles";
import { useSourceForm } from "./useSourceForm";
import { SourceFormFooter } from "./SourceFormFooter";

export type QuickNoteFormat = "plain" | "markdown" | "richText";

type QuickNoteFormValues = { name: string; format: QuickNoteFormat; content: string };

interface QuickNoteFormProps {
    parentId: string | null;
    editNode?: SourceFile | null;
    onSuccess: () => void;
    onCancel: () => void;
}

const QUICK_NOTE_PLACEHOLDER = "This quick note is collected and used as part of our research process...";

export default function QuickNoteForm({ parentId, editNode, onSuccess, onCancel }: QuickNoteFormProps) {
    const { isEdit, submitSource, renameNode } = useSourceForm(parentId, editNode ?? null, onSuccess);

    const methods = useForm<QuickNoteFormValues>({
        defaultValues: { name: editNode?.name ?? "", format: "plain", content: "" },
    });

    const onSubmit = async (data: QuickNoteFormValues) => {
        const name = data.name?.trim() || data.content?.trim() || "Quick note";
        if (isEdit) {
            await renameNode(name);
            return;
        }
        await submitSource({
            type: "quick_note",
            name,
            more: { content: data.content?.trim() ?? "", format: data.format },
        });
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <FormLabel className={formSubtitleClass}>
                    {isEdit ? "Edit Quick Note" : "Create Quick Note"}
                </FormLabel>
                <FTPInput
                    name="name"
                    label="Name"
                    className="mb-3"
                    size="small"
                    placeholder="Quick note title"
                />
                <FormLabel className="mb-1 block text-sm font-medium text-gray-700">Text type</FormLabel>
                <Controller
                    name="format"
                    control={methods.control}
                    render={({ field }) => (
                        <RadioGroup
                            row
                            className="mb-3"
                            name={field.name}
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value as QuickNoteFormat)}
                        >
                            <FormControlLabel value="plain" control={<Radio size="small" />} label="Plain" />
                            <FormControlLabel
                                value="markdown"
                                control={<Radio size="small" />}
                                label="Markdown"
                            />
                            <FormControlLabel
                                value="richText"
                                control={<Radio size="small" />}
                                label="Rich text"
                            />
                        </RadioGroup>
                    )}
                />
                <Controller
                    name="content"
                    control={methods.control}
                    render={({ field }) => (
                        <NotesTextarea
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={QUICK_NOTE_PLACEHOLDER}
                            containerClassName="min-h-[280px] mb-4"
                            textareaClassName="min-h-[250px] resize-y"
                        />
                    )}
                />
                <SourceFormFooter onCancel={onCancel} submitLabel={isEdit ? "Save" : "Add"} />
            </form>
        </FormProvider>
    );
}
